import React from 'react';
import { Helmet } from "react-helmet";
import Reveal from '../../components/ui/Reveal';
import Button from '../../components/ui/Button';
import { support } from '../../content/support';
import { trackClick } from '../../firebase/analytics';
import './home-styles.css';

const contributions = [
  { name: 'Ford Motors', href: 'https://www.ford.com/', element: 'ford_motors_link' },
  { name: 'General Motors', href: 'https://www.gm.com/gm-brands', element: 'general_motors_link' },
  { name: 'Cerence', href: 'https://www.cerence.com/', element: 'cerence_link' },
  { name: 'Ritchie Brothers', href: 'https://rbglobal.com/', element: 'ritchie_bros_link' },
  { name: 'JioSaavn', href: 'https://www.jiosaavn.com/corporate/', element: 'jiosaavn_link' },
  { name: 'Jugnoo', href: 'https://www.jugnoo.in/', element: 'jugnoo_link' },
  { name: 'Reliance Jio', href: 'https://www.jio.com/', element: 'jio_link' },
  { name: 'e-Procurement Technologies', href: 'http://www.eptl.in/pt/cc/home', element: 'eProcurement_link' },
  { name: 'Simform Solutions', href: 'https://www.simform.com/', element: 'simform_link' },
];

const socials = [
  { href: 'https://www.linkedin.com/in/kevallangalia/', element: 'linkedin_link', icon: 'fab fa-linkedin', label: 'LinkedIn' },
  { href: 'https://github.com/thekeval', element: 'github_link', icon: 'fab fa-github', label: 'GitHub' },
  { href: 'https://x.com/kevallangalia', element: 'x_twitter_link', icon: 'fa-brands fa-x-twitter', label: 'X' },
  { href: 'https://www.instagram.com/kevallangalia', element: 'instagram_link', icon: 'fab fa-instagram', label: 'Instagram' },
  { href: 'https://stackoverflow.com/users/2049623/keval-langalia', element: 'stackoverflow_link', icon: 'fab fa-stack-overflow', label: 'Stack Overflow' },
  // Last, and tinted apart from the profile icons so it reads as an offer, not a sixth badge.
  { href: support.href, element: support.element, icon: support.icon, label: support.label, support: true },
];

const elsewhere = [
  {
    href: 'https://modernandroid.dev/',
    element: 'modern_android_link',
    text: 'Modern Android Development',
    icon: 'fab fa-android',
    external: true,
  },
  {
    label: 'Contact',
    href: 'mailto:hello@keval.io',
    element: 'email_contact_link',
    text: 'hello@keval.io',
    icon: 'fas fa-envelope',
  },
];

const Home = () => {
  return (
    <main id="main" className="home" tabIndex={-1}>
      <Helmet>
        <title>Keval's Portfolio</title>
        <meta name="description" content="Welcome to Keval's personal portfolio website." />
        <link rel="canonical" href="https://keval.io" />
      </Helmet>

      <Reveal as="header" className="home-hero" delay={0}>
        <img src="profile.png" alt="Keval Langalia" className="kds-avatar home-avatar" />
        <p className="kds-eyebrow kds-eyebrow--accent home-role">Senior Software Engineer · Android</p>
        <h1 className="kds-display-md home-name">Keval Langalia</h1>
        <p className="kds-body-lg home-intro">Full-stack developer with a passion for building innovative software solutions. Experienced in everything Android (Mobile, Automotive, TV, Wearable) as well as proficient in iPhone and Windows applications.</p>
        <p className="kds-body-lg home-intro">Developing software and products for over 12 years. Currently contributing to the future of Automotive at Ford Motors.</p>

        <div className="kds-cluster home-actions">
          <Button to="/cv" onClick={trackClick('home', 'view_cv_link')}>View CV</Button>
          <Button to="/apps" onClick={trackClick('home', 'view_apps_link')}>
            <i className="fas fa-briefcase" aria-hidden="true"></i> Portfolio
          </Button>
          <Button to="/blog" onClick={trackClick('home', 'view_blog_link')}>
            <i className="fas fa-feather-pointed" aria-hidden="true"></i> Blog
          </Button>
        </div>
      </Reveal>

      <div className="home-panels">
        <Reveal as="section" className="kds-pane home-panel" delay={80} aria-labelledby="home-contributions-title">
          <h2 id="home-contributions-title" className="kds-eyebrow">Contributions</h2>
          <p className="kds-body-sm kds-muted home-panel-note">Products and platforms I have helped ship.</p>
          <ul className="kds-list home-marks">
            {contributions.map((entry, index) => (
              <li key={entry.element} style={{ '--kds-stagger-index': index }}>
                <a
                  className="home-mark"
                  href={entry.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={trackClick('home', entry.element)}
                >
                  {entry.name}
                </a>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal as="section" className="kds-pane home-panel" delay={140} aria-labelledby="home-elsewhere-title">
          <h2 id="home-elsewhere-title" className="kds-eyebrow">Elsewhere</h2>
          <ul className="kds-list kds-list--rows home-elsewhere">
            {elsewhere.map((item) => (
              <li key={item.element}>
                <span className="kds-list__icon" aria-hidden="true"><i className={item.icon}></i></span>
                {item.label && <span className="kds-body-sm kds-muted home-elsewhere-label">{item.label}</span>}
                <a
                  className="kds-link-underline kds-wrap-anywhere"
                  href={item.href}
                  {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  onClick={trackClick('home', item.element)}
                >
                  {item.text}
                </a>
              </li>
            ))}
          </ul>

          <hr className="kds-divider" />

          <ul className="kds-list kds-cluster home-socials" aria-label="Social and support links">
            {socials.map((social) => (
              <li key={social.element}>
                <a
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={trackClick('home', social.element)}
                  aria-label={social.label}
                  title={social.label}
                  className={`kds-icon-btn home-social${social.support ? ' home-social--support' : ''}`}
                >
                  <i className={social.icon} aria-hidden="true"></i>
                </a>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </main>
  );
};

export default Home;
